import { useTranslation } from 'react-i18next';
import { useUserStore } from 'src/store/user-store';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconButton,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';

type Props = {
  sx?: SxProps<Theme>;
};

const MARKETPLACE_WIDGET_URL = import.meta.env.VITE_MARKETPLACE_WIDGET_URL;
const MARKETPLACE_WIDGET_ORIGIN = new URL(MARKETPLACE_WIDGET_URL).origin;

const isMarketplaceOpenMessage = (data: unknown): data is { open: boolean } =>
  typeof data === 'object' &&
  data !== null &&
  'open' in data &&
  typeof data.open === 'boolean';

const isMarketplaceOpenMessageEvent = (
  event: MessageEvent,
  source: Window | null | undefined,
  origin: string,
): event is MessageEvent<{ open: boolean }> =>
  event.source === source &&
  event.origin === origin &&
  isMarketplaceOpenMessage(event.data);

export default function MarketplaceTrigger({ sx }: Props) {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const [open, setOpen] = useState<boolean>(false);

  const { i18n } = useTranslation();

  const { viewContext } = useUserStore();

  const theme = useTheme();

  const { mode } = theme.palette;

  const isDevEnvironment = import.meta.env.DEV;

  const shortEnvName = isDevEnvironment ? 'dev' : 'prod';

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sendMessage = useCallback(() => {
    const message = {
      theme: mode,
      view: viewContext,
      env: shortEnvName,
      locale: i18n.language,
    };

    iframeRef.current?.contentWindow?.postMessage(
      message,
      MARKETPLACE_WIDGET_ORIGIN,
    );
  }, [i18n.language, mode, shortEnvName, viewContext]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        isMarketplaceOpenMessageEvent(
          event,
          iframeRef.current?.contentWindow,
          MARKETPLACE_WIDGET_ORIGIN,
        )
      ) {
        setOpen(event.data.open);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={() => setOpen(prev => !prev)}
        sx={{
          display: 'inline-flex',
          ...sx,
        }}
        size={mdDown ? 'small' : 'large'}>
        <AppsIcon color="primary" />
      </IconButton>
      {open && (
        <iframe
          title="Marketplace Widget"
          ref={iframeRef}
          id="marketplaceWidget"
          src={MARKETPLACE_WIDGET_URL}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 50,
          }}
          onLoad={sendMessage}
        />
      )}
    </>
  );
}
